import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Add } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField, SelectField } from 'components/form';
import { PaymentType } from '@trnsact/trnsact-shared-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';

import { dealSheetConfigurationApi, useChargesConfiguration } from '../../api';
import { dealSheetConfigurationMapper } from '../../mappers';

const salesCenterFormSchema = z.object({
  saleType: z.string().trim(),
  location: z.string().trim(),
  paymentType: z.string().trim(),
  equipmentType: z.string().trim(),
  address: z.object({
    state: z.string(),
    county: z.string(),
    country: z.string(),
  }),
});

export const SalesCenterForm = () => {
  const { isConfigurationCreating, handleCreateConfiguration } = useChargesConfiguration();

  const { data: countriesResponse, loading: countriesOptionsLoading } = useQuery(
    dealSheetConfigurationApi.getCountries
  );

  const { data: regionsResponse, loading } = useQuery(dealSheetConfigurationApi.getRegions, {
    variables: { input: { country: 'US' } },
  });

  const { control, handleSubmit, formState, reset } = useForm({
    mode: 'all',
    resolver: zodResolver(salesCenterFormSchema),
    defaultValues: {
      saleType: '',
      location: '',
      paymentType: '',
      equipmentType: '',
      address: {
        state: '',
        county: '',
        country: '',
      },
    },
  });

  const handleSubmitDealRules = async (formData: Record<string, any>) => {
    const dto = dealSheetConfigurationMapper.processSalesCenterFormToAPIDto(formData);

    await handleCreateConfiguration(dto, api => ({
      refetchQueries: [api.getConfigs],
      awaitRefetchQueries: true,
    }));

    reset();
  };

  return (
    <Stack className="salesCenterCard" gap="1rem">
      <Typography variant="h6" component="span">
        Deal Rules
      </Typography>

      <Box sx={{ gap: '1rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <SelectField
          control={control}
          name="paymentType"
          variant="outlined"
          label="Payment type"
          options={convertEnumToSelectOptions(PaymentType)}
        />

        <SelectField
          name="address.country"
          label="Country"
          control={control}
          variant="outlined"
          isLoading={countriesOptionsLoading}
          options={countriesResponse?.dealSheets?.result?.list ?? []}
        />

        <SelectField
          name="address.state"
          label="State"
          control={control}
          variant="outlined"
          isLoading={loading}
          options={regionsResponse?.dealSheets?.result?.list ?? []}
        />

        <SelectField
          control={control}
          name="address.county"
          variant="outlined"
          label="County"
          isLoading={loading}
          options={regionsResponse?.dealSheets?.result?.list ?? []}
        />

        <InputField
          label="Equipment type"
          control={control}
          name="equipmentType"
          inputProps={{
            variant: 'outlined',
          }}
        />
      </Box>

      <Button
        variant="contained"
        startIcon={<Add />}
        sx={{ alignSelf: 'flex-end' }}
        onClick={handleSubmit(handleSubmitDealRules)}
        loading={formState.isSubmitting || isConfigurationCreating}
      >
        Add Rule
      </Button>
    </Stack>
  );
};
